import * as Sentry from "@sentry/vue"
import { buildApplicationRequest, translateCustomFieldErrorPath } from "~/utils/applicationMapper"
import Logger from "@/utils/Logger"
import { ApplicationType } from "~/utils/enums/ApplicationType"
import { trackApplicationSubmit } from "~/composables/TrackEvents"
import { useMatomoPageViewId } from "~/composables/useMatomoPageViewId"
import type { CustomField } from "~/types/application"

/**
 * Composable to handle application form submission
 *
 * @param formConfig The form configuration
 * @param jobId The ID of the job offer
 * @param apiConfigurationName The ATS application customer name from the job
 * @param externalId The external ID of the job offer
 * @param externalReferenceId The external reference ID of the job offer
 * @param applicationType The application type from the job
 * @returns Submit handler and submission state
 */
export function useApplicationFormData(
  formConfig: FormConfig | null,
  jobId: string | null,
  apiConfigurationName: string | null,
  externalId: string | null,
  externalReferenceId: string | null,
  applicationType: ApplicationType | null,
) {
  const { $api } = useNuxtApp()
  const { tenantId } = useHeaderStore()
  const { data: jobShop } = useNuxtData("jobShopData")
  const { t } = useI18n()

  const isSubmitting = ref(false)
  const submitError = ref<string | null>(null)
  const submitSuccess = ref(false)
  const externalFieldErrors = ref<Record<string, string>>({})
  const genericBackendError = ref<string | null>(null)

  /**
   * Handles form submission
   * @param submitData The submit data emitted from CRFormRenderer
   */
  const handleSubmit = async (submitData: {
    valid: boolean
    values: Record<string, any>
    errors: Record<string, any>
  }) => {
    if (!submitData.valid)
      return

    if (!jobId) {
      submitError.value = "Job ID is required"
      return
    }

    isSubmitting.value = true
    submitError.value = null
    submitSuccess.value = false

    try {
      trackApplicationSubmit("form_submit")

      if (!formConfig) {
        submitError.value = "Form configuration is required"
        return
      }

      // Determine which ID to use based on application type
      const effectiveJobId
        = applicationType === ApplicationType.FAST_APPLICATION
          ? externalId
          : jobId

      if (!effectiveJobId) {
        submitError.value = "Job ID is required"
        return
      }

      // Get query headers and convert to customFields
      const queryHeaders = useQueryHeaders()
      const customFields: CustomField[] = queryHeaders.value.map(header => ({
        name: header.key,
        value: header.value,
      }))

      // Add Matomo page view ID to custom fields
      const { getMatomoPageViewId } = useMatomoPageViewId()
      const matomoPageViewId = getMatomoPageViewId()
      if (matomoPageViewId) {
        customFields.push({
          name: "matomo_id_page_view",
          value: matomoPageViewId,
        })
      }

      // Build the application request from form data
      const applicationRequest = await buildApplicationRequest(
        submitData.values,
        formConfig,
        {
          jobID: effectiveJobId,
          apiConfigurationName: apiConfigurationName || "email",
          applicationType,
          externalReferenceId: externalReferenceId ?? undefined,
        },
        customFields,
      )

      // Send the application
      await $api.application.sendApplication(
        tenantId,
        formConfig.form.emailLocale || jobShop.value?.locale,
        applicationRequest,
      )

      submitSuccess.value = true
      trackApplicationSubmit("success")
    }
    catch (error: any) {
      externalFieldErrors.value = {}
      genericBackendError.value = null

      const data
        = (error
        && (error.data || error.response?.data || error.response?._data))
        || error?.data
      const errors = Array.isArray(data?.errors) ? data.errors : []

      if (errors.length > 0) {
        const fieldErrors: Record<string, string> = {}
        for (const item of errors) {
          if (item?.field && item?.message) {
            const translated = formConfig
              ? translateCustomFieldErrorPath(item.field, formConfig, submitData.values)
              : null
            fieldErrors[translated ?? item.field] = item.message
          }
        }
        externalFieldErrors.value = fieldErrors
      }
      else {
        genericBackendError.value
          = data?.description || t("application.generic-error")
      }

      submitError.value = genericBackendError.value
      const statusCode = error?.status || error?.response?.status
      Sentry.captureException(error, {
        tags: {
          feature: "application-form",
          ...(data?.uuid && { requestId: data.uuid }),
          ...(statusCode && { statusCode: String(statusCode) }),
        },
        extra: {
          jobId,
          applicationType,
          ...(data ? { responseBody: data } : { errorMessage: error?.message || String(error) }),
        },
      })
      Logger().error("Error submitting application:", error)
      trackApplicationSubmit("error")
    }
    finally {
      isSubmitting.value = false
    }
  }

  return {
    handleSubmit,
    isSubmitting,
    submitError,
    submitSuccess,
    externalFieldErrors,
    genericBackendError,
  }
}
